import { ArrowRight, PlayCircle } from "lucide-react";
import { useEffect, useState } from "react";

type AuthState = "checking" | "needs-auth" | "authenticated";

function isSafeRedirectUrl(url: string, allowedOrigin: string): boolean {
  try {
    if (url.startsWith("/") && !url.startsWith("//")) return true;
    return new URL(url).origin === allowedOrigin;
  } catch {
    return false;
  }
}

export default function Hero() {
  const allowedOrigin =
    window.location.hostname === "localhost" ? "http://localhost:3021" : "https://agents.anter.ai";

  const iframeSrc = `${allowedOrigin}/login`;

  // Hydrate from localStorage synchronously to avoid layout shift on reload
  const [authState, setAuthState] = useState<AuthState>(() => {
    try {
      const val = localStorage.getItem("anter:authenticated");
      if (val === "true") return "authenticated";
      if (val === "false") return "needs-auth";
      return "checking";
    } catch {
      return "checking";
    }
  });

  useEffect(() => {
    // Fallback: if no postMessage arrives within 3s, reveal the iframe anyway
    const fallback = setTimeout(() => {
      setAuthState((s) => (s === "checking" ? "needs-auth" : s));
    }, 3000);

    function handler(e: MessageEvent) {
      if (e.origin !== allowedOrigin) return;

      if (e.data?.type === "anter:authenticated") {
        clearTimeout(fallback);
        setAuthState("authenticated");
        try {
          localStorage.setItem("anter:authenticated", "true");
        } catch {
          /* noop */
        }
        document.documentElement.classList.add("anter-is-authenticated");
        window.dispatchEvent(new CustomEvent("anter:authenticated"));
      } else if (e.data?.type === "anter:needs-auth") {
        clearTimeout(fallback);
        setAuthState("needs-auth");
        try {
          localStorage.setItem("anter:authenticated", "false");
        } catch {
          /* noop */
        }
        document.documentElement.classList.remove("anter-is-authenticated");
        window.dispatchEvent(new CustomEvent("anter:needs-auth"));
      } else if (e.data?.type === "anter:redirect" && typeof e.data.url === "string") {
        if (isSafeRedirectUrl(e.data.url, allowedOrigin)) {
          window.location.href = e.data.url;
        } else {
          console.error("[anter] Blocked unsafe redirect:", e.data.url);
        }
      }
    }

    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
      clearTimeout(fallback);
    };
  }, [allowedOrigin]);

  const isAuth = authState === "authenticated";

  return (
    <section className="pb-4 pt-24 sm:pt-28 lg:pt-32">
      {/* Two-column on desktop; collapses to centered 1-col when authenticated */}
      <div
        className={`grid items-center gap-10 lg:gap-12 ${
          isAuth ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-[2fr_3fr]"
        }`}
      >
        {/* ── Left: live app iframe ── */}
        {!isAuth && (
          <div className="order-2 lg:order-1">
            <div className="hero-app-frame">
              {/* Mock browser chrome */}
              <div className="hero-app-chrome">
                <div className="hero-app-dots">
                  <span />
                  <span />
                  <span />
                </div>
                <span className="hero-app-url">agents.anter.ai</span>
                <div className="w-13" />
              </div>

              {/* iframe body + skeleton */}
              <div className="hero-app-body">
                {authState === "checking" && (
                  <div className="hero-app-skeleton">
                    <div className="hero-app-skeleton-bar" style={{ width: "60%" }} />
                    <div className="hero-app-skeleton-bar" style={{ width: "100%", height: 40 }} />
                    <div className="hero-app-skeleton-bar" style={{ width: "30%", height: 12 }} />
                    <div className="hero-app-skeleton-bar" style={{ width: "100%", height: 40 }} />
                    <div className="hero-app-skeleton-bar" style={{ width: "100%", height: 40 }} />
                  </div>
                )}
                <iframe
                  src={iframeSrc}
                  className="hero-app-iframe"
                  title="Sign in to Anter"
                  loading="eager"
                  style={{ opacity: authState === "needs-auth" ? 1 : 0 }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Right (or center when auth): copy ── */}
        <div
          className={`order-1 flex flex-col lg:order-2 ${
            isAuth ? "items-center text-center" : "items-start text-left"
          }`}
        >
          {/* Eyebrow */}
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary-border bg-primary-dim px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-primary-text">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            Now in early access
          </div>

          {/* Headline */}
          <h1 className="mb-5 font-display text-5xl font-bold leading-[1.1] tracking-[-0.01em] text-text sm:text-6xl md:text-7xl">
            <span className="block">Anter: Build AI agents. </span>
            <em className="block italic text-primary" style={{ fontStyle: "italic" }}>
              Ship them as APIs.
            </em>
          </h1>

          {/* Sub */}
          <p className="mb-3 max-w-xl text-lg leading-relaxed text-text-muted">
            Anter is the AI agent builder for engineering teams. It's a managed multi-agent
            orchestration engine, visual builder, and streaming API — ship AI-powered features
            without months of custom infrastructure work.
          </p>
          <p className="mb-8 max-w-lg text-base leading-relaxed text-text-faint">
            Your prototype works. Now make it production-ready.
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col gap-3 sm:flex-row sm:items-center ${
              isAuth ? "justify-center" : ""
            }`}
          >
            {isAuth ? (
              <a
                href={allowedOrigin}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary bg-primary px-6 py-3 text-sm font-bold text-primary-text-strong transition hover:opacity-90"
              >
                Open Dashboard
                <ArrowRight className="h-4 w-4" />
              </a>
            ) : (
              <a
                href="https://agents.anter.ai"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary bg-primary px-6 py-3 text-sm font-bold text-primary-text-strong transition hover:opacity-90"
              >
                Start Building Free
                <ArrowRight className="h-4 w-4" />
              </a>
            )}
            <a
              href="#demo"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-bg-secondary px-6 py-3 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover"
            >
              <PlayCircle className="h-4 w-4 text-primary" />
              Watch it run
            </a>
          </div>

          {/* Trust micro-copy */}
          {!isAuth && (
            <p className="mt-4 text-xs text-text-faint">
              Free to start &middot; No credit card &middot; First agent running in minutes &middot;
              1M free tokens, then bring your own model key
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
